import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import TextAreaWithSuggestions from "@/pages/(app)/returns/refund/validate/text-area-with-suggestion";

import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { ArticleItem } from "../article-item/article-item";
import { useLineItemRejectReasons } from "@/pages/(app)/returns/refund/hooks/use-line-item-reject-reason";
import { useMemo } from "react";
import useTranslation from "@babackapp/translations/use-translation";
import { useRejectLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-list-section";
import useRejectLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-quantities-summary";
import { useRejectLineItem } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-context";
import {
  useSetNotTreatedByMerchant,
  useNotTreatedByMerchant,
} from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-state-context";

export default function ArticlesToHandleByMerchant() {
  const t = useTranslation();
  const { itemsToShowToHandle } = useRejectLineItemsListSections();
  const isAdmin = useViewerHasAtLeastAdminRights();
  const summary = useRejectLineItemsQuantitiesSummary({
    state: "not-treated-by-merchant",
  });

  if (summary.max <= 0 || !isAdmin) return null;

  return (
    <AccordionItem value="toHandleByMerchant">
      <AccordionTrigger>
        <span>
          {t("misc.todo")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsToShowToHandle.map((lineItem, index) => (
            <ToHandleByMerchantArticle
              key={index + lineItem.line_item_id}
              lineItem={lineItem}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function ToHandleByMerchantArticle({
  lineItem,
}: {
  lineItem: ReturnRequestLineItemDto;
}) {
  const { item, updateResolution, updateVariant } = useRejectLineItem(lineItem);
  const quantityValue = useNotTreatedByMerchant(lineItem.line_item_id);
  const updateQuantity = useSetNotTreatedByMerchant(lineItem.line_item_id);
  const { addLineItemReason, lineItemsReason } = useLineItemRejectReasons();
  const editineItemReason = addLineItemReason(lineItem.line_item_id);

  const selectLineItemReason = useMemo(() => {
    return lineItemsReason[lineItem.line_item_id]?.reason;
  }, [lineItemsReason, lineItem.line_item_id]);

  return (
    <>
      <ArticleItem
        lineItem={item}
        quantity={quantityValue.selectedQuantity}
        maxQuantity={quantityValue.maxQuantity}
        onChangeQuantity={updateQuantity}
        onChangeVariant={updateVariant}
        onChangeResolution={updateResolution}
        hideStatus
        hideResolution
      />
      {quantityValue.selectedQuantity > 0 && (
        <TextAreaWithSuggestions
          onChangeReason={editineItemReason}
          reason={selectLineItemReason}
        />
      )}
    </>
  );
}
